import { useEffect, useRef, useState } from "react";
import Particles from "./Particles";

// Session flag so we never nag the same visitor twice in one browser session.
const SESSION_FLAG = "ph_exit_popup_shown";

// Depth gate: popup can only arm after the visitor has genuinely engaged with
// the landing page. 70% of scrollable height is roughly "past the free
// calculator, testimonials, comparison and the How-It-Works section" on the
// current landing layout — a reader who has clearly considered the offer.
const DEPTH_GATE = 0.70;

// Idle-after-depth-gate: only arms AFTER the depth gate is passed and only
// counts idle time during which the visitor makes no further downward scroll
// progress. Genuine "stalled reading" — not "just landed and paused to read".
const IDLE_MS = 60_000;

// Rapid upward scroll signature (mobile only): ≥ 120px in a burst, faster than
// 2 px/ms. Feels like a real "flick up to leave" gesture, not a slow re-read.
const UP_DY_MIN = 120;
const UP_DY_PER_MS_MIN = 2;

export default function ExitIntentPopup({ enabled = true }) {
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const [channel, setChannel] = useState("email"); // "email" | "whatsapp"
  const [value, setValue] = useState("");
  const [state, setState] = useState("idle"); // idle | submitting | success | error
  const [message, setMessage] = useState("");

  const depthReached = useRef(false);
  const maxScrollY = useRef(0);
  const lastScrollY = useRef(0);
  const lastScrollT = useRef(0);
  const idleTimer = useRef(null);
  const alreadyShown = useRef(false);

  const canShow = () => {
    if (!enabled) return false;
    if (alreadyShown.current) return false;
    try {
      if (sessionStorage.getItem(SESSION_FLAG) === "1") return false;
    } catch (_e) { /* sessionStorage unavailable — fail open */ }
    return true;
  };

  const show = (reason) => {
    if (!canShow()) return;
    if (!depthReached.current) return; // depth gate — hard requirement
    alreadyShown.current = true;
    try {
      sessionStorage.setItem(SESSION_FLAG, "1");
      sessionStorage.setItem(SESSION_FLAG + "_reason", reason);
    } catch (_e) { /* noop */ }
    setOpen(true);
    reportEvent("shown", reason);
  };

  const dismiss = () => {
    setClosing(true);
    reportEvent("dismissed", null);
    setTimeout(() => {
      setOpen(false);
      setClosing(false);
    }, 200);
  };

  const reportEvent = (event, reason) => {
    const backend = process.env.REACT_APP_BACKEND_URL;
    if (!backend) return;
    try {
      fetch(`${backend}/api/popup/event`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ event, reason, source: "landing_exit" }),
        keepalive: true,
      }).catch(() => {});
    } catch (_e) { /* noop */ }
  };

  const submit = async (e) => {
    e.preventDefault();
    const v = (value || "").trim();
    if (!v) {
      setState("error");
      setMessage(channel === "email" ? "Please enter your email." : "Please enter your WhatsApp number.");
      return;
    }
    if (channel === "email" && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v)) {
      setState("error");
      setMessage("That doesn't look like a valid email.");
      return;
    }
    if (channel === "whatsapp" && !/^\+?\d{7,15}$/.test(v.replace(/[\s-]/g, ""))) {
      setState("error");
      setMessage("Enter a valid mobile number (10 digits or with country code).");
      return;
    }
    setState("submitting");
    setMessage("");
    const backend = process.env.REACT_APP_BACKEND_URL;
    try {
      const res = await fetch(`${backend}/api/leads/capture`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ channel, value: v, source: "landing_exit" }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      reportEvent("captured", channel);
      setState("success");
      setMessage(channel === "email"
        ? "Sent — check your inbox in a few minutes."
        : "We'll message you on WhatsApp shortly.");
      setTimeout(() => {
        setClosing(true);
        setTimeout(() => setOpen(false), 200);
      }, 1800);
    } catch (_e) {
      setState("error");
      setMessage("Something went wrong. Please try again in a moment.");
    }
  };

  // ---- triggers ----
  useEffect(() => {
    if (!enabled) return undefined;

    lastScrollY.current = window.scrollY;
    lastScrollT.current = Date.now();
    maxScrollY.current = window.scrollY;

    const armIdle = () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      // Only arm the idle timer once the depth gate has been crossed.
      if (!depthReached.current) return;
      idleTimer.current = setTimeout(() => show("idle_after_depth"), IDLE_MS);
    };

    const isMobile = window.matchMedia("(max-width: 768px)").matches;

    const onScroll = () => {
      const y = window.scrollY;
      const now = Date.now();
      const dy = lastScrollY.current - y; // + up, − down
      const dt = Math.max(1, now - lastScrollT.current);
      lastScrollY.current = y;
      lastScrollT.current = now;

      // Track deepest scroll to gauge engagement.
      if (y > maxScrollY.current) maxScrollY.current = y;

      // Depth gate — the ONLY thing that unlocks the popup.
      const docH = Math.max(
        document.documentElement.scrollHeight,
        document.body.scrollHeight
      ) - window.innerHeight;
      const depthPct = docH > 0 ? maxScrollY.current / docH : 0;
      if (!depthReached.current && depthPct >= DEPTH_GATE) {
        depthReached.current = true;
        armIdle();
      }

      // Rapid upward-scroll trigger (mobile only), still requires depth gate.
      if (isMobile && depthReached.current && dy > UP_DY_MIN && dy / dt > UP_DY_PER_MS_MIN) {
        show("mobile_exit_scroll");
        return;
      }

      // Any FORWARD (down) scroll progress resets the idle timer — the reader
      // is still engaging, don't nag them.
      if (depthReached.current && dy < 0) armIdle();
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    return () => {
      if (idleTimer.current) clearTimeout(idleTimer.current);
      window.removeEventListener("scroll", onScroll);
    };
  }, [enabled]);

  if (!open) return null;

  const captured = state === "success";

  return (
    <div
      className={`ei-backdrop ${closing ? "ei-closing" : ""}`}
      onClick={dismiss}
      role="dialog"
      aria-modal="true"
      data-testid="exit-intent-popup"
    >
      <div
        className={`ei-modal ${closing ? "ei-closing" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="absolute inset-0 rounded-[16px] overflow-hidden" aria-hidden>
          <Particles variant="violet" count={20} />
        </div>
        <div className="relative z-10">
          <button
            type="button"
            onClick={dismiss}
            aria-label="Close"
            className="ei-close"
            data-testid="exit-popup-close"
          >
            &times;
          </button>

          {!captured && (
            <>
              <h2 className="ei-headline">
                Leaving without your reading?
              </h2>
              <p className="ei-sub">
                Send yourself the free numerology reading — no purchase, no pressure.
                Just your Life Path and Name Number, delivered to your{" "}
                {channel === "email" ? "inbox" : "WhatsApp"}.
              </p>

              <div className="ei-divider" />

              <div className="ei-channel-tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={channel === "email"}
                  onClick={() => { setChannel("email"); setState("idle"); setMessage(""); setValue(""); }}
                  className={`ei-channel-tab ${channel === "email" ? "ei-channel-tab-active" : ""}`}
                  data-testid="exit-popup-tab-email"
                >
                  Email
                </button>
                <button
                  type="button"
                  role="tab"
                  aria-selected={channel === "whatsapp"}
                  onClick={() => { setChannel("whatsapp"); setState("idle"); setMessage(""); setValue(""); }}
                  className={`ei-channel-tab ${channel === "whatsapp" ? "ei-channel-tab-active" : ""}`}
                  data-testid="exit-popup-tab-whatsapp"
                >
                  WhatsApp
                </button>
              </div>

              <form onSubmit={submit} className="ei-form" data-testid="exit-popup-form">
                <label className="sr-only" htmlFor="ei-input">
                  {channel === "email" ? "Your email" : "Your WhatsApp number"}
                </label>
                <input
                  id="ei-input"
                  type={channel === "email" ? "email" : "tel"}
                  inputMode={channel === "email" ? "email" : "tel"}
                  autoComplete={channel === "email" ? "email" : "tel"}
                  placeholder={channel === "email" ? "you@example.com" : "10-digit mobile"}
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  className="ei-input"
                  data-testid={channel === "email" ? "exit-popup-input-email" : "exit-popup-input-whatsapp"}
                  disabled={state === "submitting"}
                />
                <button
                  type="submit"
                  className="ei-cta"
                  data-testid="exit-popup-submit"
                  disabled={state === "submitting"}
                >
                  {state === "submitting" ? "Sending..." : "Send Me My Reading"}
                </button>
              </form>

              {state === "error" && (
                <p className="ei-error" data-testid="exit-popup-error">{message}</p>
              )}

              <button
                type="button"
                onClick={dismiss}
                className="ei-maybe"
                data-testid="exit-popup-maybe"
              >
                Maybe later
              </button>
            </>
          )}

          {captured && (
            <div className="text-center" data-testid="exit-popup-success">
              <h2 className="ei-headline">Sent.</h2>
              <p className="ei-sub">{message}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
